import { css, styled } from '@mui/material';
import { IconButtonGhost, Spinner, Typography } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import {
  IcCheckRegular,
  IcChevronUpLargeRegular,
  IcErrorRhombusFill,
  IcSuccessCircleFill,
} from '@kep/kcds-icons';

interface TaskCounts {
  all: number;
  failed: number;
  in_progress: number;
  success: number;
}

interface TaskManagerSummaryProps {
  counts: TaskCounts;
  onExpand: () => void;
  /** floating(축소) 상태 여부. 아이콘만 남기고 몸통을 접는다. */
  collapsed?: boolean;
  /** 바 위로 마우스가 들어올 때(자동 접기 타이머 취소 용). */
  onMouseEnter?: () => void;
  /** 바에서 마우스가 벗어날 때(floating으로 복귀 용). */
  onMouseLeave?: () => void;
}

/**
 * 태스크 매니저 미니뷰(접힘 다크 바, 400×64).
 * @description
 * - 진행 중 1개+ → 인버스 스피너 + "N건 진행 중"(N=진행 중 수)
 * - 진행 중 0 → 체크 아이콘 + "N건 작업 완료"(N=성공+실패)
 * - 완료(성공/실패) 카운트는 값이 있을 때만 우측에 노출한다. 따라서 진행 중이면서
 *   아직 완료된 게 없으면 배지가 붙지 않고, 완료된 게 생기면 진행 중에도 함께 표시된다.
 * - chevron 버튼뿐 아니라 바 전체 영역이 펼침 트리거.
 * - `collapsed`(floating)면 아이콘만 남기고 몸통을 접는다(진행 중=스피너 / 완료=체크). 이때 같은
 *   컴포넌트가 유지되어 width(400↔64)·opacity 트랜지션으로 미니↔플로팅이 자연스럽게 모핑된다.
 */
const TaskManagerSummary = ({
  counts,
  onExpand,
  collapsed = false,
  onMouseEnter,
  onMouseLeave,
}: TaskManagerSummaryProps) => {
  const { t } = useTranslation();
  const active = counts.in_progress;
  const failed = counts.failed;
  const success = counts.success;

  const isProgress = active > 0;
  // 완료(성공+실패) 건수. 진행 중 0일 때 "N건 작업 완료"의 N으로 쓴다.
  const completedCount = success + failed;

  const label = isProgress
    ? t('layout:task_manager.summary.in_progress.label', { inProgressCount: active })
    : t('layout:task_manager.summary.completed.label', { completedCount });

  return (
    <Root
      collapsed={collapsed}
      onClick={onExpand}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={collapsed ? t('layout:task_manager.summary.open.label') : undefined}
    >
      <IconSlot>
        {isProgress ? (
          <Spinner color="inverse" size="md" />
        ) : (
          <CheckIcon>
            <IcCheckRegular width={20} height={20} />
          </CheckIcon>
        )}
      </IconSlot>

      <Collapsible collapsed={collapsed} aria-hidden={collapsed}>
        <Left>
          <Label variant="label/semibold/lg" color="text/inverse/weaker/default">
            {label}
          </Label>
        </Left>

        <Right>
          {success > 0 && (
            <CountChip tone="success">
              <IcSuccessCircleFill width={16} height={16} />
              <Typography variant="label/medium/sm" color="text/inverse/weaker/default">
                {success}
              </Typography>
            </CountChip>
          )}
          {failed > 0 && (
            <CountChip tone="danger">
              <IcErrorRhombusFill width={16} height={16} />
              <Typography variant="label/medium/sm" color="text/inverse/weaker/default">
                {failed}
              </Typography>
            </CountChip>
          )}
          <IconButtonGhost
            variant="inverse"
            size="lg"
            onClick={onExpand}
            aria-label={t('layout:task_manager.summary.expand.label')}
          >
            <IcChevronUpLargeRegular />
          </IconButtonGhost>
        </Right>
      </Collapsible>
    </Root>
  );
};

export default TaskManagerSummary;

const Root = styled('div', { shouldForwardProp: prop => prop !== 'collapsed' })<{
  collapsed: boolean;
}>(
  ({ theme, collapsed }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    box-sizing: border-box;
    height: 64px;
    /* collapsed(floating): 아이콘(24px)만 남기고 몸통을 접는다. 64 - 20 - 20 = 24 → 아이콘이 정중앙. */
    width: ${collapsed ? '64px' : '400px'};
    padding: 12px 20px;
    background-color: ${collapsed
      ? theme.color['surface/transparent/strongest/default']
      : theme.color['surface/neutral/strongest/default']};
    border-radius: 4px;
    /* boxShadow/elevation/level 2 */
    box-shadow: ${theme.shadows[2]};
    overflow: hidden;
    cursor: pointer;
    transition:
      width 0.22s ease,
      background-color 0.22s ease;
  `,
);

// 상태 아이콘 슬롯 — 축소/펼침 모두 항상 보이며 위치가 유지된다(몸통만 접힘).
const IconSlot = styled('span')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
`;

// 완료 상태 리딩 체크 아이콘. 다크 바 위라 인버스 색으로 표시하고 20×20으로 고정한다.
const CheckIcon = styled('span')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: ${theme.color['text/inverse/weak/default']};

    svg {
      width: 20px;
      height: 20px;
      aspect-ratio: 1 / 1;
    }
  `,
);

// 아이콘을 제외한 몸통(라벨·카운트·chevron). collapsed 시 페이드 아웃되고 Root 폭 축소로 클리핑된다.
const Collapsible = styled('div', { shouldForwardProp: prop => prop !== 'collapsed' })<{
  collapsed: boolean;
}>(
  ({ collapsed }) => css`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    opacity: ${collapsed ? 0 : 1};
    pointer-events: ${collapsed ? 'none' : 'auto'};
    transition: opacity 0.14s ease;
  `,
);

const Left = styled('span')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const Label = styled(Typography)`
  flex-shrink: 0;
`;

const Right = styled('span')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CountChip = styled('span', { shouldForwardProp: prop => prop !== 'tone' })<{
  tone: 'success' | 'danger';
}>(
  ({ theme, tone }) => css`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    line-height: 0;
    color: ${tone === 'success'
      ? theme.color['icon/success/default']
      : theme.color['icon/danger/default']};
  `,
);
